import apiService from '../services/ApiService';
import cookieService from '../services/CookieService';
import publicAccessService from '../services/PublicAccessService';

const STORAGE_PREFIX = 'aveChainlitContext:';
const CONTEXT_TTL_MS = 2 * 60 * 1000;

const appendQueryValue = (params, key, value) => {
  if (value !== undefined && value !== null && String(value).trim() !== '') {
    params.set(key, String(value));
  }
};

export const isRagFlowAssistant = (assistant) => {
  const provider = assistant?.proveedorIA || assistant?.ProveedorIA;
  return String(provider || '').toLowerCase() === 'ragflow'
    || Boolean(assistant?.idRagFlowChat || assistant?.IdRagFlowChat);
};

export const resolveAveAssistantId = (assistant) =>
  assistant?.idOpenAIAssistant
  || assistant?.IdOpenAIAssistant
  || assistant?.idAssistant
  || assistant?.IdAssistant
  || assistant?.idRagFlowChat
  || assistant?.IdRagFlowChat
  || assistant?.id
  || assistant?.Id;

export const resolveAveAssistantName = (assistant) =>
  assistant?.nombre
  || assistant?.Nombre
  || assistant?.name
  || assistant?.Name
  || 'Asistente AVE';

export const resolveRagFlowChatId = (assistant) =>
  assistant?.idRagFlowChat
  || assistant?.IdRagFlowChat
  || '';

export const resolveRagFlowDatasetId = (assistant) =>
  assistant?.idRagFlowDataset
  || assistant?.IdRagFlowDataset
  || '';

export const getAveChainlitUrl = () =>
  process.env.REACT_APP_CHAINLIT_AVE_URL || 'http://localhost:8000';

export const buildAveChainlitSearchParams = (context) => {
  const params = new URLSearchParams();

  appendQueryValue(params, 'ctx', context?.contextId);
  appendQueryValue(params, 'assistantId', context?.assistantId);
  appendQueryValue(params, 'idAsistente', context?.assistantId);
  appendQueryValue(params, 'assistantName', context?.assistantName);
  appendQueryValue(params, 'nombreAsistente', context?.assistantName);
  appendQueryValue(params, 'provider', 'ragflow');
  appendQueryValue(params, 'proveedor', 'ragflow');
  appendQueryValue(params, 'publicChat', context?.publicChat ? 'true' : 'false');
  appendQueryValue(params, 'publico', context?.publicChat ? 'true' : 'false');
  appendQueryValue(params, 'regionalId', context?.regionalId);
  appendQueryValue(params, 'idRegional', context?.regionalId);
  appendQueryValue(params, 'areaId', context?.areaId);
  appendQueryValue(params, 'idArea', context?.areaId);
  appendQueryValue(params, 'apiBaseUrl', context?.apiBaseUrl);
  appendQueryValue(params, 'ragFlowChatId', context?.ragFlowChatId);
  appendQueryValue(params, 'idRagFlowChat', context?.ragFlowChatId);
  appendQueryValue(params, 'ragFlowDatasetId', context?.ragFlowDatasetId);
  appendQueryValue(params, 'idRagFlowDataset', context?.ragFlowDatasetId);
  appendQueryValue(params, 'requiresPassword', context?.requiresPassword ? 'true' : 'false');

  return params;
};

export const buildAveChainlitRouteUrl = (context) => {
  const params = buildAveChainlitSearchParams(context);
  return `${window.location.origin}/ave-chat?${params.toString()}`;
};

export const buildAveChainlitIframeUrl = (chainlitUrl, context) => {
  try {
    const url = new URL(chainlitUrl, window.location.origin);
    const params = buildAveChainlitSearchParams(context);
    params.forEach((value, key) => url.searchParams.set(key, value));
    return url.toString();
  } catch (_) {
    return chainlitUrl;
  }
};

export const createAveChainlitContextFromQuery = (params) => {
  const assistantId = params.get('assistantId') || params.get('idAsistente');
  if (!assistantId) {
    return null;
  }

  const publicChatValue = params.get('publicChat') || params.get('publico') || '';
  const publicChat = ['1', 'true', 'si', 'yes'].includes(publicChatValue.toLowerCase());

  return {
    type: 'utn-ave-chainlit-context',
    contextId: params.get('ctx') || createContextId(),
    assistantId,
    assistantName: params.get('assistantName') || params.get('nombreAsistente') || 'Asistente AVE',
    publicChat,
    regionalId: params.get('regionalId') || params.get('idRegional') || null,
    areaId: params.get('areaId') || params.get('idArea') || null,
    password: '',
    publicAccessToken: publicChat ? publicAccessService.getToken() : null,
    token: publicChat ? null : cookieService.getAuthToken(),
    apiBaseUrl: apiService.baseURL,
    ragFlowChatId: params.get('ragFlowChatId') || params.get('idRagFlowChat') || '',
    ragFlowDatasetId: params.get('ragFlowDatasetId') || params.get('idRagFlowDataset') || '',
    requiresPassword: ['1', 'true', 'si', 'yes'].includes((params.get('requiresPassword') || '').toLowerCase()),
    createdAt: new Date().toISOString(),
    expiresAt: Date.now() + CONTEXT_TTL_MS,
  };
};

export const consumeAveChainlitContext = (contextId) => {
  if (!contextId) {
    return null;
  }

  const key = `${STORAGE_PREFIX}${contextId}`;
  const raw = window.localStorage.getItem(key);
  window.localStorage.removeItem(key);

  if (!raw) {
    return null;
  }

  const context = JSON.parse(raw);
  if (!context?.expiresAt || Date.now() > context.expiresAt) {
    return null;
  }

  return context;
};

const cleanupExpiredContexts = () => {
  Object.keys(window.localStorage)
    .filter((key) => key.startsWith(STORAGE_PREFIX))
    .forEach((key) => {
      try {
        const context = JSON.parse(window.localStorage.getItem(key));
        if (!context?.expiresAt || Date.now() > context.expiresAt) {
          window.localStorage.removeItem(key);
        }
      } catch (_) {
        window.localStorage.removeItem(key);
      }
    });
};

const createContextId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const openAveChainlitChat = (assistant, options = {}) => {
  const assistantId = resolveAveAssistantId(assistant);
  if (!assistantId) {
    throw new Error('No se encontro el identificador del Asistente AVE.');
  }

  cleanupExpiredContexts();

  const contextId = createContextId();
  const publicChat = Boolean(options.publicChat);
  const requiresPassword = Boolean(
    options.requiresPassword
    ?? assistant?.requierePassword
    ?? assistant?.RequierePassword
    ?? false
  );
  const context = {
    type: 'utn-ave-chainlit-context',
    contextId,
    assistantId,
    assistantName: resolveAveAssistantName(assistant),
    publicChat,
    regionalId: options.regionalId || null,
    areaId: options.areaId || null,
    password: options.password || '',
    publicAccessToken: publicChat ? publicAccessService.getToken() : null,
    token: publicChat ? null : cookieService.getAuthToken(),
    apiBaseUrl: apiService.baseURL,
    ragFlowChatId: resolveRagFlowChatId(assistant),
    ragFlowDatasetId: resolveRagFlowDatasetId(assistant),
    requiresPassword,
    createdAt: new Date().toISOString(),
    expiresAt: Date.now() + CONTEXT_TTL_MS,
  };

  if (publicChat && !context.publicAccessToken) {
    throw new Error('No hay token publico para abrir el Asistente AVE.');
  }

  if (!publicChat && !context.token) {
    throw new Error('No hay sesion activa para abrir el Asistente AVE.');
  }

  const storageKey = `${STORAGE_PREFIX}${contextId}`;
  window.localStorage.setItem(storageKey, JSON.stringify(context));

  const url = buildAveChainlitRouteUrl(context);
  const opened = window.open(url, '_blank');

  if (opened) {
    opened.opener = null;
    return opened;
  }

  if (!opened && options.fallbackToSameTab) {
    window.location.assign(url);
    return null;
  }

  window.localStorage.removeItem(storageKey);
  throw new Error('No se pudo abrir la nueva pestana del Asistente AVE.');
};
